import { AxisLabelsFormatterContextObject, Options, YAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStock from 'highcharts/highstock';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import {
    Currency,
    DemeterDataFrequency,
    DemeterFilterTimeSpan,
    DemeterUserType,
    MarketIndicatorOutlook,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventActionsEnum, EventCategoriesEnum, EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import {
    areaRangeChartType,
    chartColors,
    ChartContext,
    ChartOptionsDefinitions,
    defalutLineWidth,
    defaultChartAxisTickLabelStyle,
    defaultChartAxisTitleStyle,
    defaultChartOptions,
    defaultZoneAxis,
    featuredLineWidth,
    IChartAreaRangeDataSeries,
    IChartDataSeries,
    IChartProps,
    lineChartType,
} from '../ChartDefinitions';
import chartService from '../ChartService';
import { getTimestampMap } from '../FilterTimeSpans/FilterTimeSpans';

export type MarketIndicatorColors =
    | typeof scssVariables.veryBullishColorAlpha
    | typeof scssVariables.bullishColorAlpha
    | typeof scssVariables.plainWhiteAlpha
    | typeof scssVariables.bearishColorAlpha
    | typeof scssVariables.veryBearishColorAlpha;

export type PlotBand = {
    fromDate: number;
    toDate: number;
    color: MarketIndicatorColors;
};

export interface IMarketIndicatorChartRawProps extends IChartProps {
    lineSeries: IChartDataSeries[];
    areaRangeSeries?: IChartAreaRangeDataSeries[];
    plotBands: PlotBand[];
    hideNavigator?: boolean;
    currencies?: Currency[];
    unitOfMeasures?: UnitOfMeasure[];
    yAxisLabelPrimary?: string;
    yAxisLabelSecondary?: string;
    useColorPalletteInLegend?: boolean;
    displayDecimalPlacesMinimum?: number;
    displayDecimalPlacesMaximum?: number;
    isIndicatorTechnicalPerformance?: boolean;
    navigatorTimeRange?: DemeterFilterTimeSpan;
    disableTooltip?: boolean;
}

const lineSeriesBufferPercent = 5;

const MarketIndicatorChartRaw: React.FC<IMarketIndicatorChartRawProps> = (props: IMarketIndicatorChartRawProps) => {
    const chartReference = useRef<HighchartsReact.RefObject>();

    // Application hooks.
    const currentUserType = useApplicationSelector(selectUserType);
    const [translations] = useLanguage();

    // Chart hooks.
    const marketsChartDefaultOptions = useMemo<Options>(
        () => ({
            ...defaultChartOptions,
            yAxis: [
                {
                    title: {
                        text: '',
                        style: defaultChartAxisTitleStyle,
                    },
                    labels: {
                        format: '{value:{point.y: , .0f}',
                        style: defaultChartAxisTickLabelStyle,
                    },
                    maxPadding: lineSeriesBufferPercent / 100.0,
                    minPadding: lineSeriesBufferPercent / 100.0,
                },
                {
                    title: {
                        text: '',
                        style: defaultChartAxisTitleStyle,
                    },
                    opposite: true,
                    labels: {
                        format: '{value:{point.y: , .0f}',
                        style: defaultChartAxisTickLabelStyle,
                    },
                    maxPadding: lineSeriesBufferPercent / 100.0,
                    minPadding: lineSeriesBufferPercent / 100.0,
                },
            ],
            series: [],
        }),
        [],
    );

    const [highchartOptions, setHighchartOptions] = useState<Options>(marketsChartDefaultOptions);

    const getDataSeriesDefinitions = useCallback(() => {
        const areasDataSeries = props.areaRangeSeries?.map((areaSeries, index) => {
            const currentColor = chartColors.areaChartColorsRuleSetTwo[index];

            return {
                ...chartService.getDataSeriesBase(areaSeries, areaRangeChartType, currentColor),
                yAxis: 1,
                events: {
                    legendItemClick() {
                        loggingService.trackEventWithAnalytics(
                            EventActionsEnum.ButtonClick,
                            EventCategoriesEnum.LegendItemClicked,
                            areaSeries.label,
                            EventDataTargetsEnum.MarketIndicatorChart,
                        );
                    },
                },
                tooltip: {
                    enabled:
                        (currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner) &&
                        props.disableTooltip !== true,
                    customTooltipPerSeries() {
                        const context = this as unknown as ChartContext;
                        return chartService.getAreaRangeTooltipText(context);
                    },
                },
                zIndex: 1,
            };
        });

        const linesDataSeries = props.lineSeries.flatMap((lineSeries, index) => {
            const featuredColor = props.plotBands.length ? scssVariables.black : scssVariables.ruleSetTwoLineColor1;
            const currentColor = index === 0 ? featuredColor : lineSeries.color ?? chartColors.lineChartColorsRuleSetOne[index - 1];
            const primaryLine = index === 0 || lineSeries.isPrimaryLine;
            return [
                {
                    ...chartService.getDataSeriesBase(lineSeries, lineChartType, currentColor),
                    yAxis: lineSeries.yAxis ?? 0,
                    events: {
                        legendItemClick() {
                            loggingService.trackEventWithAnalytics(
                                EventActionsEnum.ButtonClick,
                                EventCategoriesEnum.LegendItemClicked,
                                lineSeries.label,
                                EventDataTargetsEnum.MarketIndicatorChart,
                            );
                        },
                    },
                    zoneAxis: defaultZoneAxis,
                    turboThreshold: 0,
                    tooltip: {
                        enabled:
                            (currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner) &&
                            props.disableTooltip !== true,
                        customTooltipPerSeries() {
                            const context = this as unknown as ChartContext;
                            const minimumValue = Math.min(...props.lineSeries[context.series.index].data.map((x) => Math.abs(x.value)).filter((x) => !!x));
                            const numberOfDecimalPlaces = formattingService.getDisplayDecimalPlacesMinimumForCharts(minimumValue ?? 0);

                            return chartService.getTooltipText(context, {
                                dataFrequency: DemeterDataFrequency.Weekly,
                                displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? numberOfDecimalPlaces,
                                displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? numberOfDecimalPlaces,
                            });
                        },
                    },
                    lineWidth: primaryLine ? featuredLineWidth : defalutLineWidth,
                    zIndex: 2,
                },
            ];
        });

        if (props.useColorPalletteInLegend) {
            const additionalFields = [
                chartService.getDataSeriesBase(
                    {
                        label: translations.marketIndicatorOutlook[MarketIndicatorOutlook.Bearish],
                        data: [],
                    },
                    areaRangeChartType,
                    props.isIndicatorTechnicalPerformance ? scssVariables.bearishColorAlpha : scssVariables.bearishColor,
                    true,
                ),
                chartService.getDataSeriesBase(
                    {
                        label: translations.marketIndicatorOutlook[MarketIndicatorOutlook.Bullish],
                        data: [],
                    },
                    areaRangeChartType,
                    props.isIndicatorTechnicalPerformance ? scssVariables.bullishColorAlpha : scssVariables.bullishColor,
                    true,
                ),
            ];

            const usesFullColorPlotbands = props.plotBands.some(
                (x) => x.color === scssVariables.veryBearishColorAlpha || x.color === scssVariables.veryBullishColorAlpha,
            );

            if (usesFullColorPlotbands) {
                additionalFields.unshift(
                    chartService.getDataSeriesBase(
                        {
                            label: translations.marketIndicatorOutlook[MarketIndicatorOutlook.VeryBearish],
                            data: [],
                        },
                        areaRangeChartType,
                        scssVariables.veryBearishColorAlpha,
                        true,
                    ),
                );

                additionalFields.push(
                    chartService.getDataSeriesBase(
                        {
                            label: translations.marketIndicatorOutlook[MarketIndicatorOutlook.VeryBullish],
                            data: [],
                        },
                        areaRangeChartType,
                        scssVariables.veryBullishColorAlpha,
                        true,
                    ),
                );
            }

            return [...linesDataSeries, ...(areasDataSeries ?? []), ...additionalFields];
        }

        return [...linesDataSeries, ...(areasDataSeries ?? [])];
    }, [props.lineSeries, props.areaRangeSeries, props.disableTooltip]);

    // Main useEffect to update chart when props or data changes.
    useEffect(() => {
        if (props.lineSeries.length === 0) {
            return;
        }

        const dataSeries = getDataSeriesDefinitions();
        const allSeries = [...props.lineSeries, ...(props.areaRangeSeries ?? [])];
        const minimumValue = Math.min(...props.lineSeries[0].data.map((x) => Math.abs(x.value)).filter((x) => !!x));
        const numberOfDecimalPlaces = formattingService.getDisplayDecimalPlacesMinimumForCharts(minimumValue ?? 0);
        const downloadData = chartService.getDownloadDataForAreaRange(allSeries, dataSeries as ChartOptionsDefinitions[], numberOfDecimalPlaces);

        const newOptions = {
            ...marketsChartDefaultOptions,
            ...{ series: dataSeries },
            ...{
                xAxis: {
                    ...marketsChartDefaultOptions.xAxis,
                    plotBands: props.plotBands.map((x) => ({
                        from: x.fromDate,
                        to: x.toDate,
                        color: x.color,
                    })),
                    scrollbar: {
                        enabled: false,
                    },
                },
            },
            ...{ yAxis: [...(marketsChartDefaultOptions.yAxis as YAxisOptions[])!] },
            ...{
                navigator: {
                    ...highchartOptions.navigator,
                    ...{
                        enabled: !props.hideNavigator,
                    },
                    maskFill: scssVariables.ruleSetTwoAreaColor1,
                },
            },
            tooltip: {
                // Highcharts only reads enabled from the chart level tooltip, so the per series flag alone never hides it.
                enabled: props.disableTooltip !== true,
                formatter() {
                    return (this as unknown as ChartContext).series.tooltipOptions.customTooltipPerSeries.call(this);
                },
            },
            downloadData,
            exporting: {
                chartOptions: {
                    series: dataSeries.map((x) => {
                        if (x.type === 'arearange') {
                            return { ...x, color: x.color, opacity: 0.7 };
                        }
                        return x;
                    }),
                    xAxis: {
                        ...marketsChartDefaultOptions.xAxis,
                        plotBands: props.plotBands.map((x) => ({
                            from: x.fromDate,
                            to: x.toDate,
                            color: x.color,
                            opacity: 0.7,
                        })),
                        scrollbar: {
                            enabled: false,
                        },
                    },
                },
            },
        };

        props.unitOfMeasures?.forEach((x, index) => {
            if (index > props.lineSeries.length - 1) {
                return;
            }

            (newOptions.yAxis as YAxisOptions[])[index].labels!.formatter = function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                const context = this as unknown as AxisLabelsFormatterContextObject;
                return formattingService.formatNumber(context.value, { precision: numberOfDecimalPlaces });
            };
            (newOptions.yAxis as YAxisOptions[])[index].title!.text = props.yAxisLabelPrimary
                ? props.yAxisLabelPrimary
                : props.lineSeries[index]?.axisLabel ?? chartService.getCurrencyAndUnitOfMeasureText(x, props.currencies![index]);
        });

        if (newOptions.yAxis[1] && props.lineSeries[1]) {
            const currentAxisMinimumValue = Math.min(...props.lineSeries[1].data.map((y) => Math.abs(y.value)).filter((y) => !!y));
            const currentAxisNumberOfDecimalPlaces = formattingService.getDisplayDecimalPlacesMinimumForCharts(currentAxisMinimumValue ?? 0);

            (newOptions.yAxis as YAxisOptions[])[1].labels!.formatter = function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                const context = this as unknown as AxisLabelsFormatterContextObject;
                return formattingService.formatNumber(context.value, { precision: currentAxisNumberOfDecimalPlaces });
            };

            (newOptions.yAxis as YAxisOptions[])[1].title!.text = props.yAxisLabelSecondary
                ? props.yAxisLabelSecondary
                : props.lineSeries[1].axisLabel ?? chartService.getCurrencyAndUnitOfMeasureText(props.unitOfMeasures![1], props.currencies![1]);
        }
        setHighchartOptions(newOptions as Options);
    }, [
        props.lineSeries,
        props.areaRangeSeries,
        props.unitOfMeasures,
        props.currencies,
        props.yAxisLabelSecondary,
        props.displayDecimalPlacesMinimum,
        props.displayDecimalPlacesMaximum,
        // The restricted data source arrives after the series, so the options have to be rebuilt when the tooltip gate flips.
        props.disableTooltip,
    ]);

    useEffect(() => {
        if (props.hideNavigator || !props.lineSeries.length || !chartReference.current) {
            return;
        }

        const oldestDate = Math.min(...props.lineSeries.map((x) => x.data[0]?.asOfDate.getTime()));
        const latestDataDate = Math.max(...props.lineSeries.map((x) => x.data[x.data.length - 1]?.asOfDate.getTime()));
        const timeRange = props.navigatorTimeRange ? props.navigatorTimeRange : DemeterFilterTimeSpan.OneYear;
        const minimumDateForTimeSpan = getTimestampMap(new Date(latestDataDate))[timeRange];

        chartReference.current?.chart.xAxis[0].setExtremes(Math.max(minimumDateForTimeSpan, oldestDate), latestDataDate);
    }, [props.hideNavigator, props.lineSeries, chartReference.current]);

    return (
        <HighchartsReact
            ref={(newChartReference: HighchartsReact.RefObject) => {
                if (chartReference.current || !newChartReference) {
                    return;
                }

                chartReference.current = newChartReference;
                if (props.chartReference) {
                    props.chartReference(newChartReference);
                }
            }}
            highcharts={HighStock}
            options={highchartOptions}
            containerProps={{ style: { height: '100%' } }}
        />
    );
};

export default memo(MarketIndicatorChartRaw);
